/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type Store from "@ember-data/store";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import type { EmberChangeset } from "ember-changeset";
import { Changeset } from "ember-changeset";
import lookupValidator from "ember-changeset-validations";
import { validateNumber } from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import { getNewExpectedDosages } from "parog-web/components/bio-exam-results/utils/dosage-expected";
import getReferenceValuesMessage from "parog-web/components/bio-exam-results/utils/reference-value-message";
import type { ExpectedDosage } from "parog-web/models/bio-exam-expected-dosage-changed-journal-event";
import BioExamResult from "parog-web/models/bio-exam-result";
import type BioExamObservation from "parog-web/models/loinc";
import type Patient from "parog-web/models/patient";
import formatNumberAsString from "parog-web/utils/format-number-as-string";
import trimLoincCode from "parog-web/utils/trim-loinc-code";
import type { S as LabeledInputSig } from "ui-kit/components/changeset/labeled-input";
import styles from "./component.module.css";

interface S {
  Args: Pick<LabeledInputSig["Args"], "changeset"> & {
    patient: Patient;
    bioExamObservation: BioExamObservation;
    validateOnResultUpdate: (isInvalid: boolean) => void;
  };
}

export default class BioExamGenericFieldComponent extends Component<S> {
  styles = styles;

  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked
  declare referenceValueChangeset: EmberChangeset;

  @tracked
  shouldDisplayReferenceRange = false;

  @tracked
  cleanedValue;

  @tracked
  operator;

  shouldDisplayOperator;

  constructor(owner: unknown, args: S["Args"]) {
    super(owner, args);

    // The auto-validation is disabled, and we call validate() only when losing the focus on the field
    // The @action onFocusOut
    this.referenceValueChangeset = Changeset(
      {},
      lookupValidator(this.validationObject),
      this.validationObject,
      {
        skipValidate: true
      }
    );

    const result = this.args.changeset
      .get("results")
      .find(res => res.code == this.propertyName) as BioExamResult;

    this.shouldDisplayOperator = result && result.operator != "equal-to";
    this.operator = result?.operator;
  }

  get loinc() {
    return this.args.bioExamObservation.loinc;
  }

  get valueType() {
    return this.args.bioExamObservation.valueType;
  }

  get referenceValuesMessage() {
    return this.args.bioExamObservation.referenceValuesMessage;
  }

  get propertyName() {
    return `LOINC-${this.loinc}`;
  }

  get expectedDosage(): ExpectedDosage | undefined {
    const newExpectedDosages = getNewExpectedDosages(this.args.patient) as
      | ExpectedDosage[]
      | undefined;

    if (newExpectedDosages) {
      return newExpectedDosages[`LOINC-${this.loinc}`];
    }

    return undefined;
  }

  get minReferenceValue() {
    return (
      this.expectedDosage?.min || this.args.bioExamObservation.minReferenceValue
    );
  }

  get maxReferenceValue() {
    return (
      this.expectedDosage?.max || this.args.bioExamObservation.maxReferenceValue
    );
  }

  get referenceValues() {
    if (this.referenceValuesMessage) return this.referenceValuesMessage;

    return getReferenceValuesMessage({
      min: this.minReferenceValue,
      max: this.maxReferenceValue,
      prefix: "Valeurs de référence : "
    });
  }

  get isZeroable() {
    return this.args.bioExamObservation.zeroable;
  }

  get validationObject() {
    const referenceValuesValidations = [
      // The number validation is declared on the global form, to prevent the user to write anything that's not a number
      // We put it here also to avoid the validations below to be displayed at the same time because we don't to display
      // Both validations message (validations are tested in the declaration order, and only one is message displayed).
      validateNumber({ string: true, allowBlank: true, message: " " })
    ];

    if (!this.isZeroable) {
      referenceValuesValidations.push(
        validateNumber({
          gt: 0,
          allowBlank: true,
          message: "Cette valeur doit être supérieure à 0"
        })
      );
    }

    const validationObject = {};
    validationObject[this.propertyName] = referenceValuesValidations;
    return validationObject;
  }

  @action
  updateValue(event) {
    const target = event.target as HTMLInputElement;

    this.args.changeset.set(this.propertyName, target.value);
    this.onChangesetUpdate(this.args.changeset);
  }

  @action
  updateOperator(operator) {
    const results = this.args.changeset.get("results");
    const existingResultIndex = results.findIndex(
      res => res.code == this.propertyName
    );

    const resultToUpdate = results[existingResultIndex];
    resultToUpdate.operator = operator;

    results[existingResultIndex] = resultToUpdate;

    this.args.changeset.set("results", [...results]);
    this.onChangesetUpdate(this.args.changeset);
  }

  @action
  onChangesetUpdate(value) {
    this.cleanedValue = formatNumberAsString(value.get(this.propertyName));
    this.referenceValueChangeset.set(this.propertyName, this.cleanedValue);
    const code = this.propertyName;

    const results = this.args.changeset.get("results");

    if (!code.startsWith("LOINC")) return;

    this.referenceValueChangeset.validate();

    const existingResultIndex = results?.findIndex(res => res.code == code);
    const existingResult = results[existingResultIndex];

    if (!existingResult) {
      const obs = this.store.peekRecord(
        "loinc",
        trimLoincCode(code)
      ) as BioExamObservation;

      const unit = obs.defaultUnit;

      const bioExamResult =
        this.args.changeset.get("isFromHprim") == true
          ? BioExamResult.newManualRes({
              store: this.store,
              code,
              value: this.cleanedValue,
              unit
            })
          : new BioExamResult({
              store: this.store,
              code,
              value: this.cleanedValue,
              unit
            });

      results.push(bioExamResult);
      return;
    }

    if (existingResult.isManualMatching) {
      // Update raw value
      const segs = existingResult.resultHash.originalLine.raw.segs;
      segs[4] = this.cleanedValue;
    }

    if (existingResult.value == this.cleanedValue) return;

    if (isEmpty(this.cleanedValue)) {
      this.removeResultFromChangeset(existingResultIndex);
      return;
    }

    existingResult.value = this.cleanedValue;
  }

  removeResultFromChangeset(index: string) {
    const results = this.args.changeset.get("results");
    results.splice(index, 1);
    this.args.changeset.set("results", results);
  }

  @action
  onFocusOut() {
    if (!this.minReferenceValue && !this.maxReferenceValue) return;

    if (
      (this.minReferenceValue as number) > parseFloat(this.cleanedValue) ||
      (this.maxReferenceValue as number) < parseFloat(this.cleanedValue)
    ) {
      this.shouldDisplayReferenceRange = true;
      return;
    }

    this.shouldDisplayReferenceRange = false;
  }
}
